import { create } from 'zustand';

export type ClientUser = {
  id: string;
  company_name: string;
  contact_email: string;
  tier: string;
};

interface AuthState {
  accessToken: string | null;
  expiresAt: string | null;
  user: ClientUser | null;
  setSession: (token: string, expiresAt: string, user: ClientUser) => void;
  updateUser: (user: ClientUser) => void;
  logout: () => void;
}

const STORAGE_KEY = 'dp-client-session-v1';

function persist(expiresAt: string | null, user: ClientUser | null) {
  try {
    if (!user) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify({ expiresAt, user }));
  } catch { /* quota */ }
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { accessToken: null, expiresAt: null, user: null };
    const parsed = JSON.parse(raw);
    if (!parsed.user) return { accessToken: null, expiresAt: null, user: null };
    const idle = Number(localStorage.getItem('dp-client-idle') ?? Date.now());
    if (Date.now() - idle > 60 * 60_000) {
      localStorage.removeItem(STORAGE_KEY);
      return { accessToken: null, expiresAt: null, user: null };
    }
    return { accessToken: 'cookie', expiresAt: parsed.expiresAt ?? null, user: parsed.user };
  } catch {
    return { accessToken: null, expiresAt: null, user: null };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  ...load(),
  setSession(token, expiresAt, user) {
    persist(expiresAt, user);
    try { localStorage.setItem('dp-client-idle', String(Date.now())); } catch { /* quota */ }
    set({ accessToken: token || 'cookie', expiresAt, user });
  },
  updateUser(user) {
    const { expiresAt } = useAuthStore.getState();
    persist(expiresAt, user);
    set({ user });
  },
  logout() {
    persist(null, null);
    void fetch('/client/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined);
    set({ accessToken: null, expiresAt: null, user: null });
  },
}));
